import React, { useRef } from 'react'

import styled from 'styled-components'

import { useDispatch } from 'react-redux'

import { PopoverMenu, ListItem } from '@8x8/oxygen-popover'
import ArrowDownIcon from '@8x8/oxygen-icon/novo/ArrowDownIcon'

import ComputerIcon from '~/components/icons/computer'
import JoinRoomOptionsButton from '~/components/buttons/join-options-button'
import { Translate } from '~/core/translate'
import { Flex } from '~/components/common/containers'
import Button from '~/components/common/button'

import { joinByType } from '~/actions/active-session'
import { JOIN_TYPES, CALENDAR_EVENT_PROVIDERS } from '../../core/constants'

const ViewOnlyIcon = () => (
  <ComputerIcon size='22' />
)

const ViewOnlyHeader = () => (
  <Translate>view_only_capitalized</Translate>
)

const ViewOnlyContent = () => (
  <Translate>join_meeting_view_only_description</Translate>
)

const StyledFlex = styled(Flex)`
  ${({ offset }) => offset && `
      button:first-child {
        padding-left: 68px;
      }
  `}
`

const StyledButton = styled(Button)`
  border-radius: 6px 0 0 6px;
  width: 100%;
`

const Header = styled.div`
  align-items: center;
  height: 24px;
  ${({ theme }) => ({ ...theme._neo.bodyBold02 })}
  color: ${props => props.theme.color}
`

const Description = styled.div`
  height: 56px;
  line-height: 18px;
  font-size: ${({ theme }) => theme._neo.label01.fontSize};
  align-items: center;
  color: ${props => props.theme.colorSecondary};
`

// This style was added directly from Oxygen. The initial Oxygen styles are not being used, and I didn't find out why.
const StyledPopoverMenu = styled(PopoverMenu)`
  background-color: ${({ theme }) => theme._neo.ui06};
  border: 1px solid ${({ theme }) => theme._neo.ui01};
  box-shadow: ${({ theme }) => `0px 1px 2px 0px ${theme._neo.colorGray02}40`};
  width: 280px;
  z-index: 1;

  button {
    background-color: ${({ theme }) => theme._neo.ui06};

    &:hover {
      background-color: ${({ theme }) => theme._neo.ui02};
    }

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme._neo.focus01};
      outline-offset: -2px;
    }
  }
`

const JoinRoomButton = ({
  children,
  className,
  joinArgs,
  joinType,
  offset,
  ...rest
}) => {
  const dispatch = useDispatch()
  const portalRef = useRef(null)

  const join = event => {
    event.stopPropagation?.()
    dispatch(joinByType(joinType, joinArgs))
  }

  const joinViewOnly = event => {
    event.stopPropagation?.()
    dispatch(joinByType(joinType, {
      ...joinArgs,
      viewOnly: true
    }))
  }

  return (
    <StyledFlex fullWidth className={className} ref={portalRef} offset={offset}>
      <StyledButton
        onClick={join}
        {...rest}
      >
        {children}
      </StyledButton>
      <StyledPopoverMenu
        portalTargetRef={portalRef}
        placement='bottom-end'
        items={[
          {
            key: '1',
            children: (
              <ListItem
                leadingVisuals={<ViewOnlyIcon />}
                secondaryText={
                  <Description>
                    <ViewOnlyContent />
                  </Description>
                }
              >
                <Header>
                  <ViewOnlyHeader />
                </Header>
              </ListItem>
            )
          }
        ]}
        onClick={joinViewOnly}
      >
        <JoinRoomOptionsButton
          onClick={event => {
            event.stopPropagation?.()
          }}
          isDisabled={joinArgs?.provider !== CALENDAR_EVENT_PROVIDERS.EGHT && joinType === JOIN_TYPES.URL}
          {...rest}
        >
          {/* Exporting a wrapper for this from join-options-button breaks the styling. Not sure why */}
          <ArrowDownIcon size={20} />
        </JoinRoomOptionsButton>
      </StyledPopoverMenu>
    </StyledFlex>
  )
}

export default JoinRoomButton
